import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { isApiErrorEnvelope } from '@/api/form-errors'
import { useFlipUserStatus, useUsers, type UsersFilter } from '@/users/api'
import type { AdminUserListItem, PlanFilter, UserStatus } from '@/users/schemas'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface ErrorBanner {
  message: string
  traceId?: string
}

const STATUS_FILTER_VALUES: UserStatus[] = ['pending_email', 'active', 'disabled', 'deleted']

const STATUS_LABEL_KEY: Record<UserStatus, string> = {
  pending_email: 'users.statusPendingEmail',
  active: 'users.statusActive',
  disabled: 'users.statusDisabled',
  deleted: 'users.statusDeleted',
}

function formatIsoDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('hu-HU')
  } catch {
    return '—'
  }
}

/**
 * `/users` — admin user-lista (DEC-ADMIN-004 / DEC-ADMIN-006 / DEC-ADMIN-014).
 *
 * Szűrők: státusz-chipek + DÁP / csomag selectek + regisztráció-dátumtartomány
 * (azonnaliak), email+név keresés (submitre). Lapozás a backend paginátorával.
 * Soronként link a részletlapra (/users/{ulid}) + status-flip: a LETILTÁSHOZ
 * kötelező indok-űrlap (DEC-ADMIN-014 — a moderation_actions naplóba), a
 * feloldás `window.confirm`. A `pending_email`/`deleted` nem flip-elhető.
 */
export function UserList() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState<UserStatus | undefined>(undefined)
  const [dapFilter, setDapFilter] = useState<'' | '1' | '0'>('')
  const [planFilter, setPlanFilter] = useState<'' | PlanFilter>('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [activeWithin, setActiveWithin] = useState('')
  const [abuseBannedFilter, setAbuseBannedFilter] = useState(false)
  const [emailFilter, setEmailFilter] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState<{ email: string; name: string }>({
    email: '',
    name: '',
  })
  const [page, setPage] = useState(1)
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null)
  // A letiltás inline indok-űrlapja (melyik sor + a beírt indok + a
  // visszaélés-ban jelölő — DEC-DAP-024).
  const [disabling, setDisabling] = useState<{
    id: string
    reason: string
    abuseBan: boolean
  } | null>(null)

  const filter: UsersFilter = {
    status: statusFilter,
    email: searchQuery.email || undefined,
    name: searchQuery.name || undefined,
    dapVerified: dapFilter === '' ? undefined : dapFilter === '1',
    plan: planFilter === '' ? undefined : planFilter,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
    activeWithinDays: activeWithin === '' ? undefined : Number(activeWithin),
    abuseBanned: abuseBannedFilter || undefined,
    page,
  }
  const users = useUsers(filter)
  const flip = useFlipUserStatus()

  function resetPage() {
    setPage(1)
  }

  function onSearchSubmit(event: React.FormEvent) {
    event.preventDefault()
    setSearchQuery({ email: emailFilter.trim(), name: nameFilter.trim() })
    resetPage()
  }

  function flipError(error: unknown) {
    if (isApiErrorEnvelope(error)) {
      setErrorBanner({ message: error.message, traceId: error.trace_id })
    } else {
      setErrorBanner({ message: t('users.flipError') })
    }
  }

  function onEnable(user: AdminUserListItem) {
    if (user.id == null) return
    setErrorBanner(null)
    if (!window.confirm(t('users.flipEnableConfirm'))) return
    flip.mutate({ id: user.id, status: 'active' }, { onError: flipError })
  }

  function onDisableConfirm() {
    if (disabling === null || disabling.reason.trim().length < 3) return
    setErrorBanner(null)
    flip.mutate(
      {
        id: disabling.id,
        status: 'disabled',
        reason: disabling.reason.trim(),
        abuseBan: disabling.abuseBan,
      },
      {
        onError: flipError,
        onSuccess: () => setDisabling(null),
      },
    )
  }

  const meta = users.data?.meta
  const items = users.data?.items ?? []

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('users.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('users.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('users.intro')}</p>
      </header>

      <nav className="flex flex-wrap gap-2" aria-label={t('users.statusLabel')}>
        <Button
          type="button"
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setStatusFilter(undefined)
            resetPage()
          }}
        >
          {t('users.filterAll')}
        </Button>
        {STATUS_FILTER_VALUES.map((value) => (
          <Button
            key={value}
            type="button"
            variant={statusFilter === value ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setStatusFilter(value)
              resetPage()
            }}
          >
            {t(STATUS_LABEL_KEY[value])}
          </Button>
        ))}
      </nav>

      {/* DEC-ADMIN-014 — további szűrők: DÁP / csomag / regisztráció-dátum. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('users.dapFilterLabel')}</span>
          <select
            className="rounded-md border bg-background px-2 py-1.5"
            value={dapFilter}
            onChange={(e) => {
              setDapFilter(e.target.value as '' | '1' | '0')
              resetPage()
            }}
          >
            <option value="">{t('users.filterAll')}</option>
            <option value="1">{t('users.dapVerifiedOption')}</option>
            <option value="0">{t('users.dapUnverifiedOption')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('users.planFilterLabel')}</span>
          <select
            className="rounded-md border bg-background px-2 py-1.5"
            value={planFilter}
            onChange={(e) => {
              setPlanFilter(e.target.value as '' | PlanFilter)
              resetPage()
            }}
          >
            <option value="">{t('users.filterAll')}</option>
            <option value="premium">{t('users.planPremium')}</option>
            <option value="free">{t('users.planFree')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('users.activeWithinLabel')}</span>
          <select
            className="rounded-md border bg-background px-2 py-1.5"
            value={activeWithin}
            onChange={(e) => {
              setActiveWithin(e.target.value)
              resetPage()
            }}
          >
            <option value="">{t('users.filterAll')}</option>
            {['1', '3', '7', '30'].map((days) => (
              <option key={days} value={days}>
                {t('users.activeWithinOption', { days })}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 self-end pb-1.5 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={abuseBannedFilter}
            onChange={(e) => {
              setAbuseBannedFilter(e.target.checked)
              resetPage()
            }}
          />
          {t('users.abuseBannedFilter')}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('users.createdFromLabel')}</span>
          <input
            type="date"
            className="rounded-md border bg-background px-2 py-1.5"
            value={createdFrom}
            onChange={(e) => {
              setCreatedFrom(e.target.value)
              resetPage()
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('users.createdToLabel')}</span>
          <input
            type="date"
            className="rounded-md border bg-background px-2 py-1.5"
            value={createdTo}
            onChange={(e) => {
              setCreatedTo(e.target.value)
              resetPage()
            }}
          />
        </label>
      </div>

      <form onSubmit={onSearchSubmit} className="flex flex-col gap-2 sm:flex-row">
        <Input
          type="search"
          inputMode="email"
          value={emailFilter}
          onChange={(event) => setEmailFilter(event.target.value)}
          placeholder={t('users.emailSearchPlaceholder')}
          aria-label={t('users.emailSearchLabel')}
        />
        <Input
          type="search"
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          placeholder={t('users.nameSearchPlaceholder')}
          aria-label={t('users.nameSearchLabel')}
        />
        <Button type="submit" variant="secondary">
          {t('users.search')}
        </Button>
      </form>

      {errorBanner && (
        <Alert variant="destructive">
          <AlertDescription>
            {errorBanner.message}
            {errorBanner.traceId && (
              <span className="block text-xs opacity-70">trace ID: {errorBanner.traceId}</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {users.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {users.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('users.loadError')}</AlertDescription>
        </Alert>
      )}

      {!users.isPending && !users.isError && items.length === 0 && (
        <p className="text-muted-foreground">{t('users.empty')}</p>
      )}

      {!users.isPending && !users.isError && items.length > 0 && (
        <ul className="flex flex-col gap-3">
          {items.map((user, index) => {
            const rowDisabling = disabling !== null && disabling.id === user.id ? disabling : null
            return (
              <li
                key={user.id ?? `idx-${index}`}
                className="flex flex-col gap-1 rounded-lg border p-3"
              >
                <div className="flex items-baseline gap-2 text-sm">
                  {user.id != null ? (
                    <Link
                      to={`/users/${user.id}`}
                      className="font-medium text-primary underline underline-offset-4"
                    >
                      {user.nick_name || user.email || user.id}
                    </Link>
                  ) : (
                    <span className="font-medium">{user.nick_name || user.email || '—'}</span>
                  )}
                  {user.nick_name && <span className="truncate">{user.email ?? '—'}</span>}
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">
                    {t(STATUS_LABEL_KEY[(user.status ?? 'active') as UserStatus])}
                  </span>
                  {user.dap_verified && (
                    <span
                      className="rounded-full bg-verified/10 px-1.5 py-0.5 text-xs font-medium text-verified ring-1 ring-verified/30"
                      title={t('users.dapVerifiedTooltip')}
                    >
                      ✓
                    </span>
                  )}
                  {user.abuse_banned && (
                    <span
                      className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-xs font-medium text-destructive ring-1 ring-destructive/30"
                      title={t('users.abuseBanBadgeTooltip')}
                    >
                      {t('users.abuseBanBadge')}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-baseline gap-x-3 text-xs text-muted-foreground">
                  <span>
                    {t('users.lastActiveLabel')}: {formatIsoDate(user.last_active_at)}
                  </span>
                  <span>
                    {t('users.lastLoginLabel')}: {formatIsoDate(user.last_login_at)}
                  </span>
                  <span>
                    {t('users.createdAtLabel')}: {formatIsoDate(user.created_at)}
                  </span>
                </div>
                {(user.status === 'active' || user.status === 'disabled') && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {user.status === 'disabled' ? (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => onEnable(user)}
                        disabled={flip.isPending}
                      >
                        {t('users.flipEnable')}
                      </Button>
                    ) : rowDisabling !== null ? (
                      <div className="flex w-full flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 sm:flex-row sm:items-center">
                        <Input
                          value={rowDisabling.reason}
                          onChange={(e) =>
                            setDisabling((d) => (d ? { ...d, reason: e.target.value } : d))
                          }
                          placeholder={t('users.reasonPlaceholder')}
                          aria-label={t('users.reasonLabel')}
                        />
                        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs">
                          <input
                            type="checkbox"
                            className="size-4"
                            checked={rowDisabling.abuseBan}
                            onChange={(e) =>
                              setDisabling((d) => (d ? { ...d, abuseBan: e.target.checked } : d))
                            }
                          />
                          {t('users.abuseBanLabel')}
                        </label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={onDisableConfirm}
                            disabled={flip.isPending || rowDisabling.reason.trim().length < 3}
                          >
                            {t('users.flipDisableConfirmButton')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setDisabling(null)}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() =>
                          user.id != null &&
                          setDisabling({ id: user.id, reason: '', abuseBan: false })
                        }
                        disabled={flip.isPending}
                      >
                        {t('users.flipDisable')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* Lapozó (DEC-ADMIN-014) — a backend eddig is lapozott, most látszik is. */}
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between gap-2 text-sm">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={users.isPending || meta.current_page <= 1}
          >
            {t('users.prevPage')}
          </Button>
          <span className="text-muted-foreground">
            {t('users.pageStatus', {
              current: meta.current_page,
              last: meta.last_page,
              total: meta.total,
            })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={users.isPending || meta.current_page >= meta.last_page}
          >
            {t('users.nextPage')}
          </Button>
        </div>
      )}
    </main>
  )
}
